import React from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';

import dataAids from '../../../../constants/dataAids';
import { VIDEO_TYPE } from '../../../../constants/constants';
import { LAYOUT1, LAYOUT3 } from '../../../../constants/pdpLayouts';
import imageCropOptions from '../../../../constants/imageCropOptions';
import { isZoomEnabled } from '../../utils/ProductUtils';
import { getImageRatio } from '../../utils/ProductUtils';
import { useModal } from '../../../hooks/useModal';

import { Carousel } from '../../lazy';
import MobileCarouselControl from './MobileCarouselControl';
import Video from '../Video';
import ZoomLightbox from '../details/ZoomLightbox';

function getScaledImage(image, { pdpImageCropMethod, pdpImageShape, pdpLayout }) {
  const assetApiUrl = /[rs=w:](\d+)/.exec(image);
  if (assetApiUrl) {
    const curSize = +assetApiUrl[1];
    const size = curSize * getImageRatio(pdpImageShape, pdpLayout);
    if (pdpImageCropMethod === imageCropOptions.EXPAND_TO_FILL) {
      return image.replace(`w:${curSize},h:${curSize}`, `w:${size},h:${size},cg:true`);
    }
  }

  return image;
}

const getStyles = (pdpImageShape, pdpLayout) => ({
  carouselItemWrapper: {
    position: 'relative',
    paddingTop: `${100 / getImageRatio(pdpImageShape, pdpLayout)}%`,
    paddingRight: '100%'
  },
  carouselItem: {
    position: 'absolute',
    top: '0',
    bottom: '0',
    left: '0',
    right: '0',
    display: 'flex', // flex styles to center video content
    alignItems: 'center',
    justifyContent: 'center'
  }
});

function MobileAssets(props) {
  const { Block, Image } = UX2.Element;
  const {
    productAssets,
    renderMode,
    onClickThumbnail,
    currentAssetIndex,
    pdpLayout,
    layoutProps,
    pdpImageShape,
    enableMobileZoom,
    product,
    pdpImageCropMethod,
    selectedAsset
  } = props;
  const styles = getStyles(pdpImageShape, pdpLayout);
  const [isZoomLightboxOpen, showZoomLightbox, closeZoomLightbox] = useModal();
  const { currentAssetUrl, zoomAssetUrl } = selectedAsset;
  const classicLayout = pdpLayout === LAYOUT1 || pdpLayout === LAYOUT3;
  const hasMultipleAssets = productAssets.length > 1;
  const controls = [
    {
      component: MobileCarouselControl,
      props: {
        currentAssetIndex,
        classicLayout,
        hasMultipleAssets,
        ...layoutProps
      }
    }
  ];

  const getAssetDetails = assetData => {
    let type = assetData?.type || 'image';
    let assetUrl = assetData?.large_url || assetData?.url || assetData?.attachment_file_name;
    if (assetData?.attachment_content_type === 'VIDEO' || assetData?.metadata) {
      type = VIDEO_TYPE;
      if (assetData?.metadata) {
        const parsedMetaData = JSON.parse(assetData.metadata);
        assetUrl = parsedMetaData.formatted_url;
      }
    }
    return { type, assetUrl };
  };

  return (
    <React.Fragment>
      <Carousel
        infinite={ false }
        draggable={ hasMultipleAssets }
        dots={ false }
        arrows={ false }
        renderMode={ renderMode }
        cellPadding={ 2 }
        slideWidth='100%'
        controls={ controls }
        afterChange={ nextIndex => onClickThumbnail(nextIndex, true) }
      >
        { productAssets.map((assetData, index) => {
          const assetDetails = getAssetDetails(assetData);
          const { type, assetUrl } = assetDetails;

          // Only enable the mobile zoom on published sites due poor UX in editor
          const zoomEnabled =
            enableMobileZoom && renderMode === 'PUBLISH' && isZoomEnabled(assetData);
          const isImage = assetUrl && assetUrl.indexOf('isteam') > -1;
          const expandToFill = pdpImageCropMethod === imageCropOptions.EXPAND_TO_FILL;

          const handleClick = () => {
            if (currentAssetIndex !== index) {
              onClickThumbnail(index);
            }

            showZoomLightbox();
          };

          return (
            <Block key={ index } style={ styles.carouselItemWrapper }>
              <Block style={ styles.carouselItem }>
                { type === VIDEO_TYPE || typeof assetData?.metadata === 'string' ? (
                  <Video videoUrl={ assetUrl } />
                ) : (
                  <Image
                    src={ getScaledImage(assetUrl, props) }
                    alt={ product.name }
                    data-aid={ dataAids.PRODUCT_CAROUSEL_ASSET_RENDERED }
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: isImage && expandToFill ? 'cover' : 'contain'
                    }}
                    onClick={ zoomEnabled ? handleClick : () => {} }
                  />
                ) }
              </Block>
            </Block>
          );
        }) }
      </Carousel>
      <ZoomLightbox
        src={ currentAssetUrl }
        srcLarge={ zoomAssetUrl }
        alt={ product.name }
        visible={ isZoomLightboxOpen }
        onClose={ closeZoomLightbox }
        renderMode={ renderMode }
      />
    </React.Fragment>
  );
}

MobileAssets.propTypes = {
  selectedAsset: PropTypes.object,
  enableMobileZoom: PropTypes.bool,
  layoutProps: PropTypes.object,
  productAssets: PropTypes.array,
  layout: PropTypes.string,
  assetsVisible: PropTypes.number,
  assets: PropTypes.array,
  assetChangedByVariantSelection: PropTypes.any,
  currentAssetIndex: PropTypes.number,
  onClickThumbnail: PropTypes.func,
  renderMode: PropTypes.string,
  fetching: PropTypes.bool,
  product: PropTypes.object,
  pdpImageCropMethod: PropTypes.string,
  pdpImageShape: PropTypes.string,
  pdpLayout: PropTypes.string
};

export default MobileAssets;
